import { useEffect, useRef, useState } from "react";

/**
 * Inline text editor for canvas text — opened by double-clicking a
 * label or zone caption (see `ShapeRenderer`'s `onTextEdit`). An
 * absolutely-positioned HTML input overlaid on the stage container
 * at the shape's pixel position, so the coach edits text in place
 * instead of round-tripping through the rail input.
 *
 * Enter or blur commits; Escape cancels. The parent decides what an
 * empty commit means (labels require text; zone captions clear).
 * Per the no-native-chrome rule this is a styled inline input, not
 * a `window.prompt`.
 */

export type InlineTextEditorProps = {
  /** Pixel position within the (relatively-positioned) stage
   * container the input anchors to. */
  x: number;
  y: number;
  initialValue: string;
  maxLength: number;
  placeholder?: string;
  onCommit: (text: string) => void;
  onCancel: () => void;
  "data-testid"?: string;
};

export function InlineTextEditor(props: InlineTextEditorProps) {
  const [value, setValue] = useState(props.initialValue);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // `committed` guards the blur that follows Enter/Escape from
  // double-firing the callbacks.
  const doneRef = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const commit = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    props.onCommit(value);
  };
  const cancel = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    props.onCancel();
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      maxLength={props.maxLength}
      placeholder={props.placeholder}
      data-testid={props["data-testid"] ?? "canvas-inline-text-editor"}
      className="absolute z-10 w-44 rounded border border-accent bg-canvas px-2 py-1 text-sm shadow-overlay focus:outline-none focus:ring-2 focus:ring-accent"
      style={{ left: props.x, top: props.y }}
      onKeyDown={(e) => {
        // Keep canvas shortcuts (V/A/L/... , Delete) from firing
        // while typing — the global handler also guards on INPUT,
        // but stopping propagation keeps intent explicit.
        e.stopPropagation();
        if (e.key === "Enter") commit();
        if (e.key === "Escape") cancel();
      }}
      onBlur={commit}
    />
  );
}
